import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Scale } from 'lucide-react';
import api from '../../services/api';
import { formatMoney } from '../../utils/sportSchedule';

/**
 * Dashboard card → the payments board on the matching filter.
 *   sport     "Owes money · Needs renewal"
 *   learning  "Owes money · Overdue" (regular learning centers, /payments)
 * Renders nothing when both numbers are zero or the call fails.
 */
export default function SportBalancesCard({ refreshKey = 0, kind = 'sport' }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const learning = kind === 'learning';

  useEffect(() => {
    let alive = true;
    api.get('/sport/balances/summary')
      .then((r) => { if (alive) setSummary((r?.data || r)?.summary || null); })
      .catch(() => { if (alive) setSummary(null); });
    return () => { alive = false; };
  }, [refreshKey]);

  const second = learning ? summary?.overdueCount : summary?.needsRenewalCount;
  if (!summary || (!summary.owesCount && !second)) return null;

  const base = learning ? '/payments' : '/sport/balances';
  const money = (n) => formatMoney(Number(n) || 0, i18n.language);
  const box = {
    flex: '1 1 150px', minHeight: 64, display: 'grid', gap: 2, padding: '0.7rem 0.9rem', textAlign: 'start', cursor: 'pointer',
    borderRadius: 12, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-primary)', boxShadow: 'none',
  };
  return (
    <section
      style={{ display: 'grid', gap: '0.5rem', padding: '0.8rem', borderRadius: 14, border: '1px solid var(--color-border)', background: 'var(--color-surface-alt)' }}
      data-testid="sport-balances-card"
      data-kind={kind}
    >
      <h2 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Scale size={18} aria-hidden="true" /> {t(learning ? 'learningBoard.cardTitle' : 'sport.balances.cardTitle')}
      </h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        <button type="button" style={box} onClick={() => navigate(`${base}?filter=owes`)} data-testid="sport-balances-card-owes">
          <small style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>{t('sport.balances.filter.owes')}</small>
          <strong style={{ fontSize: '1.15rem', color: summary.owesCount ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
            {t(learning ? 'learningBoard.cardOwes' : 'sport.balances.cardOwes', { count: summary.owesCount, amount: money(summary.owedTotal) })}
          </strong>
        </button>
        {learning ? (
          <button type="button" style={box} onClick={() => navigate(`${base}?filter=overdue`)} data-testid="sport-balances-card-overdue">
            <small style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>{t('learningBoard.filter.overdue')}</small>
            <strong style={{ fontSize: '1.15rem', color: summary.overdueCount ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
              {t('learningBoard.cardOverdue', { count: summary.overdueCount || 0, amount: money(summary.overdueTotal) })}
            </strong>
          </button>
        ) : (
          <button type="button" style={box} onClick={() => navigate('/sport/balances?filter=renewal')} data-testid="sport-balances-card-renewal">
            <small style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>{t('sport.balances.filter.renewal')}</small>
            <strong style={{ fontSize: '1.15rem', color: summary.needsRenewalCount ? 'var(--color-warning-dark)' : 'var(--color-text-primary)' }}>
              {t('sport.balances.cardRenewal', { count: summary.needsRenewalCount })}
            </strong>
          </button>
        )}
      </div>
    </section>
  );
}
